import { Component, ElementRef, EventEmitter, Input, OnDestroy, Output, Renderer2 } from '@angular/core';

@Component({
    selector: 'app-auto-complete-dropdown',
    templateUrl: './auto-complete-dropdown.component.html',
    styleUrls: ['./auto-complete-dropdown.component.scss'],
    host: {
        '(document:click)': 'onClick($event)',
    },
})
export class AutoCompleteDropdownComponent implements OnDestroy {
    @Input() pageType: string = '';
    @Input() disable!: boolean;
    @Input() isMultiSelect: boolean = false;
    /** When `isMultiSelect`, restores checkbox state (same shape as `app-dropdown` init). */
    @Input() initializeMultiSelect?: any;
    @Input() defaultOptionIndex!: number;
    @Input() selectedValue: string | null = null;
    /** When true, the dropdown list is appended to body and positioned fixed (use inside tables/overflow containers). */
    @Input() appendToBody: boolean = false;
    @Input() data: any = {
        label: 'Select Option',
        options: [
            { label: 'Option 1', value: 'Option 1' },
            { label: 'Option 2', value: 'Option 2' },
            { label: 'Option 3', value: 'Option 3' },
            { label: 'Option 4', value: 'Option 4' },
        ],
    };

    inputValue = { label: '', value: '' };
    showSuggestions = false;
    drop: boolean = false;
    selectedOptionText: string = 'Select Option';
    selectedOptionIndex: number = 0;
    filteredSuggestions: any = [];
    searchTerm: string = '';
    searchText: string = ''; // filter text (multi-select) or full input (single)
    selectedLabel: string = ''; // selected option label

    /** Same semantics as multiselect `app-dropdown`: keyed by option `value`. */
    selectedOptionIndexes: Record<string, { index: number; label: string; value: string; checked: boolean }> = {};

    @Output() selectedItemEvent = new EventEmitter<any>();

    private listEl: HTMLElement | null = null;
    private dropdownContainer: HTMLElement | null = null;
    private scrollListeners: Array<{ el: HTMLElement | Window; fn: () => void }> = [];
    private resizeListener: (() => void) | null = null;
    private rafId: number | null = null;

    constructor(private eref: ElementRef, private renderer: Renderer2) {}

    ngOnChanges() {
        if (this.isMultiSelect) {
            this.selectedOptionText = this.data?.label ?? 'Select Option';
            this.reloadMultiSelectionsFromParents();
            if (!this.drop) {
                this.syncMultiSelectSummaryText();
            }
            this.updateSuggestions();
            return;
        }

        this.updateSuggestions();

        if (!this.selectedValue) {
            this.searchText = '';
            this.inputValue = { label: '', value: '' };
            this.selectedOptionText = this.data?.label ?? 'Select Option';
            return;
        }

        const found = this.data?.options?.find((opt: any) => String(opt.value) === String(this.selectedValue));

        if (found) {
            this.inputValue = { ...found };
            this.searchText = found.label;
            this.selectedOptionText = found.label;
        }
    }

    ngOnInit() {
        this.updateSuggestions();
    }

    onTriggerSurfaceClick(ev: MouseEvent): void {
        const t = ev.target as HTMLElement | null;
        if (this.isMultiSelect && t?.tagName === 'INPUT') {
            return;
        }
        this.show();
    }

    onMultiInputFocus(): void {
        if (!this.isMultiSelect || this.disable) {
            return;
        }
        if (!this.drop) {
            this.drop = true;
            this.searchText = '';
            if (this.appendToBody) {
                setTimeout(() => this.moveListToBody(), 50);
            }
        }
        this.updateSuggestions();
    }

    /** Multiselect: first option with empty value resets selection (`app-dropdown` parity). */
    get multiPlaceholderRow(): { label: string; value: string } | null {
        if (!this.isMultiSelect || !Array.isArray(this.data?.options) || !this.data.options.length) {
            return null;
        }
        return this.data.options[0]?.value === '' ? this.data.options[0] : null;
    }

    private reloadMultiSelectionsFromParents(): void {
        this.selectedOptionIndexes = {};
        const init = this.initializeMultiSelect;
        if (!Array.isArray(init) || init.length === 0) {
            return;
        }
        init.forEach((item: any, idx: number) => {
            const raw = item?.value;
            if (raw === '' || raw == null || String(raw) === '') {
                return;
            }
            const v = String(raw);
            this.selectedOptionIndexes[v] = {
                index: idx,
                label: item.label,
                value: v,
                checked: true,
            };
        });
    }

    private syncMultiSelectSummaryText(): void {
        const labels = Object.values(this.selectedOptionIndexes)
            .filter((x) => x.checked)
            .map((x) => x.label);
        this.searchText = labels.join(', ');
    }

    show() {
        if (this.disable) return;

        this.drop = !this.drop;
        if (this.drop) {
            if (this.isMultiSelect) {
                this.searchText = '';
            }
            this.updateSuggestions();
            if (this.appendToBody) {
                setTimeout(() => this.moveListToBody(), 50);
            }
        } else {
            if (this.isMultiSelect) {
                this.syncMultiSelectSummaryText();
                this.updateSuggestions();
            }
            this.moveListBackToComponent();
        }
    }

    selectOption(label: string, value: string, index: number) {
        /* Legacy path for non-autocomplete markup; PDP uses typed list instead. */
        if (!this.isMultiSelect) {
            this.selectedOptionText = label;
            this.selectedOptionIndex = index;
            this.show();
            this.selectedItemEvent.emit({
                index: index,
                data: value,
            });
            return;
        }
        /* Multiselect is handled via `toggleMultiSuggestion` / `multiClearSelections`. */
    }

    onInputChange(): void {
        if (this.isMultiSelect) {
            if (!this.drop) {
                /* User edited summary inline without opening panel — treat as narrowing after next open/focus instead. */
            }
            this.updateSuggestions();
            return;
        }
        if (!this.searchText) {
            this.selectedOptionText = this.data?.label ?? 'Select Option';
            this.selectedItemEvent.emit({ data: '' });
        }

        this.updateSuggestions();
    }

    updateSuggestions(): void {
        const raw = this.data?.options ?? [];
        const term = (this.searchText || '').toLowerCase();

        if (this.isMultiSelect) {
            const withoutPlaceholder = raw.filter((s: any) => s?.value !== '' && String(s.value).length > 0);
            this.filteredSuggestions = withoutPlaceholder.filter((s: any) =>
                (s?.label ?? '').toLowerCase().startsWith(term)
            );
            if (this.drop) {
                this.showSuggestions =
                    term.length === 0 || this.filteredSuggestions.length > 0 || withoutPlaceholder.length === 0;
            } else {
                this.showSuggestions = false;
            }
            return;
        }

        this.filteredSuggestions = raw.filter((s: any) => (s?.label ?? '').toLowerCase().startsWith(term));
        this.showSuggestions = this.filteredSuggestions.length > 0;
    }

    onSuggestionClick(suggestion: any): void {
        if (this.isMultiSelect) {
            return;
        }
        this.moveListBackToComponent();
        this.drop = false;
        this.searchText = suggestion.label;
        this.selectedOptionText = suggestion.label;

        this.selectedItemEvent.emit({ data: suggestion.value });

        this.showSuggestions = false;
    }

    multiClearSelections(ev: MouseEvent): void {
        ev.preventDefault();
        ev.stopPropagation();
        this.selectedOptionIndexes = {};
        this.emitMultiSelect(true);
        this.syncMultiSelectSummaryText();
        this.updateSuggestions();
    }

    toggleMultiSuggestion(ev: MouseEvent, suggestion: any): void {
        ev.preventDefault();
        ev.stopPropagation();
        const raw = suggestion?.value;
        if (raw === '' || raw == null || String(raw) === '') {
            return;
        }
        const v = String(raw);
        if (this.selectedOptionIndexes[v]?.checked === true) {
            delete this.selectedOptionIndexes[v];
        } else {
            this.selectedOptionIndexes[v] = {
                index: 0,
                label: suggestion.label,
                value: v,
                checked: true,
            };
        }
        this.emitMultiSelect(true);
        this.updateSuggestions();
    }

    multiRowChecked(val: unknown): boolean {
        return !!(val != null && this.selectedOptionIndexes[String(val)]?.checked);
    }

    private emitMultiSelect(notifyParent: boolean): void {
        if (!notifyParent) {
            return;
        }
        const keys = Object.keys(this.selectedOptionIndexes);
        if (keys.length === 0) {
            this.selectedItemEvent.emit({ isMultiSelect: true, data: '' });
        } else {
            this.selectedItemEvent.emit({
                isMultiSelect: true,
                data: { ...this.selectedOptionIndexes },
            });
        }
    }

    onClick(event: any) {
        const clickedInsideTrigger = this.eref.nativeElement.contains(event.target);
        const clickedInsideList = this.listEl?.contains(event.target);
        if (!clickedInsideTrigger && !clickedInsideList) {
            this.moveListBackToComponent();
            this.drop = false;
            if (this.isMultiSelect) {
                this.syncMultiSelectSummaryText();
                this.updateSuggestions();
            }
            if (!this.isMultiSelect) {
                this.showSuggestions = true;
            }
        }
    }

    private moveListToBody(retryCount = 0): void {
        const container = this.eref.nativeElement.querySelector('.j-dropdown');
        const trigger = this.eref.nativeElement.querySelector('.j-dropdown__label');
        const list = this.eref.nativeElement.querySelector('.j-dropdown__list');
        if (!container || !trigger || !list) {
            if (retryCount < 3 && this.drop) {
                setTimeout(() => this.moveListToBody(retryCount + 1), 30);
            }
            return;
        }

        this.renderer.appendChild(document.body, list);
        this.updateListPositionInBody(trigger, list);
        this.listEl = list;
        this.dropdownContainer = container;
        this.attachScrollAndResizeListeners();
    }

    private updateListPositionInBody(trigger: HTMLElement, list: HTMLElement): void {
        const rect = trigger.getBoundingClientRect();
        this.renderer.setStyle(list, 'position', 'fixed');
        this.renderer.setStyle(list, 'top', `${rect.bottom + 4}px`);
        this.renderer.setStyle(list, 'left', `${rect.left}px`);
        this.renderer.setStyle(list, 'width', `${rect.width}px`);
        this.renderer.setStyle(list, 'z-index', '1000');
    }

    private getScrollParents(element: HTMLElement): (HTMLElement | Window)[] {
        const parents: (HTMLElement | Window)[] = [];
        let el: HTMLElement | null = element;
        while (el && el !== document.body) {
            const style = window.getComputedStyle(el);
            const overflow = style.overflow + style.overflowY + style.overflowX;
            if (/(auto|scroll|overlay)/.test(overflow)) {
                parents.push(el);
            }
            el = el.parentElement;
        }
        parents.push(window);
        return parents;
    }

    private attachScrollAndResizeListeners(): void {
        const updatePosition = () => {
            if (!this.listEl || !this.drop) return;
            const trigger = this.eref.nativeElement.querySelector('.j-dropdown__label');
            if (trigger) this.updateListPositionInBody(trigger, this.listEl);
        };
        const scheduleUpdate = () => {
            if (this.rafId != null) cancelAnimationFrame(this.rafId);
            this.rafId = requestAnimationFrame(() => {
                this.rafId = null;
                updatePosition();
            });
        };
        const trigger = this.eref.nativeElement.querySelector('.j-dropdown__label');
        if (trigger) {
            const scrollParents = this.getScrollParents(trigger);
            scrollParents.forEach((el) => {
                el.addEventListener('scroll', scheduleUpdate, true);
                this.scrollListeners.push({ el, fn: scheduleUpdate });
            });
        }
        this.resizeListener = () => updatePosition();
        window.addEventListener('resize', this.resizeListener);
    }

    private detachScrollAndResizeListeners(): void {
        if (this.rafId != null) {
            cancelAnimationFrame(this.rafId);
            this.rafId = null;
        }
        this.scrollListeners.forEach(({ el, fn }) => {
            el.removeEventListener('scroll', fn, true);
        });
        this.scrollListeners = [];
        if (this.resizeListener) {
            window.removeEventListener('resize', this.resizeListener);
            this.resizeListener = null;
        }
    }

    private moveListBackToComponent(): void {
        this.detachScrollAndResizeListeners();
        if (!this.listEl || !this.dropdownContainer) return;
        this.renderer.appendChild(this.dropdownContainer, this.listEl);
        this.renderer.removeStyle(this.listEl, 'position');
        this.renderer.removeStyle(this.listEl, 'top');
        this.renderer.removeStyle(this.listEl, 'left');
        this.renderer.removeStyle(this.listEl, 'width');
        this.renderer.removeStyle(this.listEl, 'z-index');
        this.listEl = null;
        this.dropdownContainer = null;
    }

    ngOnDestroy(): void {
        this.moveListBackToComponent();
    }
}
